<template>
  <BaseModal :modalTitle="$t('area.lab-equipment.create.title')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :resetCallback="formResetCallback"
      :onSubmit="onSubmit">
      <div class="mb-6">
        <UiInputTextFieldVee
          name="type"
          :label="$t('area.lab-equipment.edit.field.type')"
          :placeholder="$t('area.lab-equipment.edit.placeholder.type')"
          :options="labEquipmentTypes"
          :rules="['required']"
          isRequired
          :unit="currentLocale.toUpperCase()"
          v-model="newEquipment.type"
          @update:model-value="resetValidationMessages" />

        <UiInputTextFieldVee
          name="name"
          :label="$t('area.lab-equipment.edit.field.name')"
          :placeholder="$t('area.lab-equipment.edit.placeholder.name')"
          :rules="['required']"
          isRequired
          :unit="currentLocale.toUpperCase()"
          v-model="newEquipment.name"
          @update:model-value="resetValidationMessages" />

        <UiInputTextFieldVee
          name="serialNumber"
          :label="$t('area.lab-equipment.edit.field.serial-number')"
          :placeholder="$t('area.lab-equipment.edit.placeholder.serial-number')"
          :rules="['required']"
          isRequired
          v-model="newEquipment.serialNumber"
          @update:model-value="resetValidationMessages" />

        <UiInputCheckboxGroupVee
          name="analysisIDs"
          :label="$t('area.lab-equipment.edit.field.analyses')"
          :options="analysesOptions"
          v-model="newEquipment.analysisIDs"
          @update:model-value="resetValidationMessages" />
      </div>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { getErrorMessage } from '@/globals/typescript/utils/errorMessages'
import { useToast } from '@/globals/typescript/utils/toast'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import { useLabEquipmentStore } from '@/stores/labEquipmentStore'
import { useUserStore } from '@/stores/userStore'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { mapActions, mapState } from 'pinia'

type TCreateLabEquipmentForm = {
  type: string
  name: string
  serialNumber: string
  analysisIDs: string[]
}

function createEquipmentForm(): TCreateLabEquipmentForm {
  return {
    type: '',
    name: '',
    serialNumber: '',
    analysisIDs: [],
  }
}

export default {
  name: 'CreateLabEquipmentModal',

  emits: ['close'],

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      newEquipment: createEquipmentForm(),

      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAnalysisStore, ['allAnalyses']),
    ...mapState(useAccountStore, ['labAccount']),
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),
    ...mapState(useLabEquipmentStore, ['labEquipmentTypes']),
    ...mapState(useUserStore, ['currentUser']),

    analysesOptions(): { text: string; value: string }[] {
      return this.allAnalyses.map((analysis) => ({
        text: analysis.getName(this.currentLocale),
        value: analysis.id,
      }))
    },
  },

  methods: {
    ...mapActions(useLabEquipmentStore, ['createLabEquipment']),

    async onSubmit() {
      this.isLoading = true
      this.resetValidationMessages()

      try {
        if (!this.currentUser?.id || !this.labAccount?.id) {
          this.errorText = this.$t('error.common.standard')
          return
        }

        await this.createLabEquipment({
          labAccountID: this.labAccount.id,
          data: {
            type: this.newEquipment.type,
            name: this.newEquipment.name,
            serialNumber: this.newEquipment.serialNumber,
            analysisIDs: this.newEquipment.analysisIDs,
          },
        })

        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('area.lab-equipment.create.success.created'))
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        this.$emit('close')
      } catch (error) {
        const code = error instanceof Error ? error.message : ''

        if (code === 'already-exists') {
          this.errorText = this.$t(
            'area.lab-equipment.edit.message.serial-number-in-use'
          )
          return
        }

        this.errorText = getErrorMessage(code)
        useToast().error(this.$t('W: Kunne ikke oprette udstyr'))
      } finally {
        this.isLoading = false
      }
    },

    formResetCallback(): boolean {
      return !this.errorText
    },

    resetValidationMessages() {
      this.errorText = ''
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.newEquipment }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>

